import React from "react";
import moment from "moment";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { Tooltip } from "@my-scoot/component-library-legacy";
import { AVATAR_COLORS, DEFAULT_USER, U_LETTER } from "./constants";
import constants from "constants/constants";

export const getRecipientDisplayName = (record, isWhatsApp = false) => {
  if (
    record?.name &&
    typeof record.name === "string" &&
    record.name.trim() &&
    record.name.trim().toLowerCase() !== "unknown"
  ) {
    return record.name.trim();
  }
  if (isWhatsApp) {
    return record?.phone_number || record?.phone || DEFAULT_USER;
  }
  return record?.email || DEFAULT_USER;
};

export const getAvatarColors = (name) => {
  const hash = [...(name || U_LETTER)].reduce(
    (acc, char) => acc + char.charCodeAt(0),
    0
  );
  const colors = AVATAR_COLORS;
  return colors[hash % colors.length];
};

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return moment(dateString).format(
    constants.internationalInputDateTimeFormatWithSaparation
  );
};

export const statsColumns = (record) => [
  {
    field: "attempted_for",
    headerName: "Attempted for",
    info: "Number of users to whom the campaign was attempted to be sent",
    valueFormatter: () =>
      record?.attempted_count ?? record.total_attempted ?? 0,
  },
  {
    field: "sent_to",
    headerName: "Sent to",
    info: "Number of users to whom the campaign was sent",
    valueFormatter: () => record?.sent_to_count ?? record.sent_count ?? 0,
  },
  {
    field: "delivered_to",
    headerName: "Delivered to",
    info: "Number of users to whom the message was delivered",
    valueFormatter: () => record?.delivered_count ?? 0,
  },
  {
    field: "failed_for",
    headerName: "Failed for",
    info: "Number of users whom the campaign was not deliver (Couldn’t deliver due to spam limits, opt-outs, etc.)",
    valueFormatter: () => record?.failed_count ?? record?.total_failed ?? 0,
  },
  {
    field: "opened_by",
    headerName: "Opened by",
    info: "Number of users who the opened the campaign",
    valueFormatter: () =>
      record?.opened_count ??
      record?.open_count ??
      record?.total_opened_count ??
      0,
  },
  {
    field: "replied_by",
    headerName: "Replied by",
    info: "Number of users who replied to the message",
    valueFormatter: () => record?.total_replies ?? 0,
  },
];

export const cardContent = (user, isWhatsApp = false) => [
  {
    title: isWhatsApp ? "Phone number:" : "Email ID:",
    value: isWhatsApp
      ? user.phone_number || user.phone || "-"
      : user.email || "-",
  },
  {
    title: "Attempted at:",
    value: formatDate(user.attempted_at),
  },
  {
    title: "Sent at:",
    value: formatDate(user.sent_at),
  },
  {
    title: "Delivered at:",
    value: formatDate(user.delivered_at),
  },
  {
    title: "Opened at:",
    value: formatDate(user.read_at || user.opened_at || user.open_at),
  },
  {
    title: "Failure reason:",
    value: (
      <div className="tw-flex tw-items-center tw-gap-1 tw-justify-end">
        <span>{user.failure_reason || "N/A"}</span>
        {user.failure_reason && (
          <Tooltip
            arrow
            color="primary"
            placement="top"
            title={user.failure_reason}
          >
            <InfoOutlinedIcon
              className="tw-text-[#8A8A8A]"
              style={{ fontSize: 16, cursor: "pointer" }}
            />
          </Tooltip>
        )}
      </div>
    ),
  },
];

export const getAudienceColumnsDesktop = (isWhatsApp = false) => [
  {
    field: "name",
    headerName: "Recipient name",
    valueFormatter: (record) => {
      const displayName = getRecipientDisplayName(record, isWhatsApp);
      const colors = getAvatarColors(displayName);
      const avatarChar = displayName ? displayName[0].toUpperCase() : U_LETTER;
      return (
        <div className="tw-flex tw-items-center tw-gap-2">
          <div
            className="tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-bold"
            style={{
              backgroundColor: colors.bg,
              color: colors.text,
            }}
          >
            {avatarChar}
          </div>
          <span className="tw-text-[#493AB1] tw-font-medium tw-underline tw-cursor-pointer">
            {displayName}
          </span>
        </div>
      );
    },
    fixed: "left",
  },
  isWhatsApp
    ? {
        field: "phone_number",
        headerName: "Phone number",
        valueFormatter: (record) =>
          record?.phone_number || record?.phone || "-",
      }
    : {
        field: "email",
        headerName: "Email ID",
        valueFormatter: (record) => record?.email ?? "-",
      },
  {
    field: "attempted_at",
    headerName: "Attempted at",
    width: "200px",
    valueFormatter: (record) => formatDate(record?.attempted_at),
  },
  {
    field: "sent_at",
    headerName: "Sent at",
    width: "200px",
    valueFormatter: (record) => formatDate(record?.sent_at),
  },
  {
    field: "delivered_at",
    headerName: "Delivered at",
    width: "200px",
    valueFormatter: (record) => formatDate(record?.delivered_at),
  },
  {
    field: "read_at",
    headerName: "Opened at",
    width: "200px",
    valueFormatter: (record) =>
      formatDate(record?.read_at || record?.opened_at || record?.open_at),
  },
  {
    field: "failure_reason",
    headerName: "Failure reason",
    width: "200px",
    valueFormatter: (record) => (
      <div className="tw-flex tw-items-center tw-gap-1">
        <span>{record.failure_reason || "N/A"}</span>
        {record.failure_reason && (
          <Tooltip
            arrow
            color="primary"
            placement="top"
            title={record.failure_reason}
          >
            <InfoOutlinedIcon
              className="tw-text-[#8A8A8A]"
              style={{ fontSize: 16, cursor: "pointer" }}
            />
          </Tooltip>
        )}
      </div>
    ),
  },
];

export const audienceColumnsDesktop = getAudienceColumnsDesktop(false);
